import * as yup from 'yup';

import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import { Button, CardFormGroup } from '../../../components';
import { Card, CardBody, CardFooter, CardSubtitle, Form, FormGroup, Input, Label } from 'reactstrap';
import React, { useEffect, useState } from 'react';

import { ProgramStandard } from '../../../common/Types';
import { programService } from '../../../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const Standards = (props: AnnualReportProps) => {
    const [standard, setStandard] = useState<ProgramStandard>();
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
    const [anyNo, setAnyNo] = useState<boolean>(false);
    const [submitDirection, setSubmitDirection] = useState<AnnualReportNavigationDirection>(
        AnnualReportNavigationDirection.Forward,
    );

    const standardsSchema = yup
        .object({
            standardId: yup
                .number()
                .required()
                .default(standard?.standardId || 0),
            programId: yup.number().required().default(props.program.programId),
            anyNo: yup.boolean(),
            activeYear: yup.number().required().default(+props.reportYear),
            standardIsponsorship: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            standardIiprogramGoals: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            advisoryCommitteeRepresentation: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            advisoryCommitteeFulfilsRole: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            standardIiiresources: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            standardIvstudentAndGraduate: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            standardVfairPractices: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            standardIIAcHasMetAnnually: yup
                .boolean()
                .typeError('Please select Yes or No')
                .required('Please select Yes or No'),
            datesCommitteeMeetings: yup.string().notRequired().nullable(),
            descriptionAnalysis: yup.string().notRequired().nullable(),
            actionPlan: yup.string().notRequired().nullable(),
            createdBy: yup.string().notRequired().nullable().default(standard?.createdBy),
            dateCreated: yup.string().notRequired().nullable().default(standard?.dateCreated),
            lastEdited: yup.string().notRequired().nullable().default(standard?.lastEdited),
            editedBy: yup.string().notRequired().nullable().default(standard?.editedBy),
        })
        .defined();

    const {
        control,
        handleSubmit,
        getValues,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(standardsSchema as any),
    });

    const hasAnyNo = () => {
        const values = getValues() as ProgramStandard;

        if (values) {
            return (
                values.standardIsponsorship?.toString() === 'false' ||
                values.standardIiprogramGoals?.toString() === 'false' ||
                values.advisoryCommitteeRepresentation?.toString() === 'false' ||
                values.advisoryCommitteeFulfilsRole?.toString() === 'false' ||
                values.standardIiiresources?.toString() === 'false' ||
                values.standardIvstudentAndGraduate?.toString() === 'false' ||
                values.standardVfairPractices?.toString() === 'false' ||
                values.standardIIAcHasMetAnnually?.toString() === 'false'
            );
        }

        return false;
    };

    const onSubmit = (formData) => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving standards...');

        programService
            .saveProgramStandards(props.program.programId, formData)
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Standards Saved.',
                });

                if (submitDirection === AnnualReportNavigationDirection.Forward) {
                    props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save standards.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const submitWithoutValidation = () => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving standards...');

        const formValues = getValues();

        const values = {
            standardId: standard?.standardId || 0,
            programId: props.program.programId,
            activeYear: +props.reportYear,
            createdBy: standard?.createdBy,
            dateCreated: standard?.dateCreated,
            lastEdited: standard?.lastEdited,
            editedBy: standard?.editedBy,
            ...formValues,
        } as unknown as ProgramStandard;

        programService
            .saveProgramStandards(props.program.programId, values)
            .then((response) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Standards Saved.',
                });

                setStandard(response);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save standards.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    useEffect(() => {
        const getStandards = () => {
            programService
                .getProgramStandads(props.program.programId, props.reportYear ?? new Date().getFullYear())
                .then((response) => {
                    setStandard(response);
                    reset(response);

                    setAnyNo(
                        response.standardIsponsorship?.toString() === 'false' ||
                            response.standardIiprogramGoals?.toString() === 'false' ||
                            response.advisoryCommitteeRepresentation?.toString() === 'false' ||
                            response.advisoryCommitteeFulfilsRole?.toString() === 'false' ||
                            response.standardIiiresources?.toString() === 'false' ||
                            response.standardIvstudentAndGraduate?.toString() === 'false' ||
                            response.standardVfairPractices?.toString() === 'false' ||
                            response.standardIIAcHasMetAnnually?.toString() === 'false',
                    );
                })
                .catch(() => {
                    toast.error('Unable to get settings.');
                });
        };

        getStandards();
    }, [props.program.programId, props.reportYear, setStandard, setAnyNo, reset]);

    watch('standardIsponsorship');
    watch('standardIiprogramGoals');
    watch('advisoryCommitteeRepresentation');
    watch('advisoryCommitteeFulfilsRole');
    watch('standardIiiresources');
    watch('standardIvstudentAndGraduate');
    watch('standardVfairPractices');
    watch('standardIIAcHasMetAnnually');

    if (hasAnyNo() !== anyNo) {
        setAnyNo(hasAnyNo());
    }

    return (
        <Card>
            {standard && (
                <Form noValidate onSubmit={handleSubmit(onSubmit, (validationErrors) => {
                    const fieldNames = Object.keys(validationErrors);
                    toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
                })}>
                    <Controller
                        name="standardId"
                        control={control}
                        render={({ field }) => (
                            <input type="hidden" {...field} value={field.value ?? standard?.standardId ?? 0} />
                        )}
                    />
                    <Controller
                        name="anyNo"
                        control={control}
                        render={({ field }) => <input type="hidden" {...field} value={hasAnyNo().toString()} />}
                    />
                    <CardBody>
                        <h3 className="card-title">Standards</h3>
                        <CardSubtitle className="mb-3">
                            To the best of your knowledge, does your program meet each standard?
                        </CardSubtitle>

                        <CardFormGroup>
                            <h5>Standard I</h5>
                            <Label className="standard-label">Sponsorship</Label>
                            <Controller
                                name="standardIsponsorship"
                                control={control}
                                render={({ field }) => (
                                    <div className="radio-group">
                                        <div className="custom-control custom-radio">
                                            <input
                                                type="radio"
                                                id={'standardIsponsorshipYes'}
                                                className="custom-control-input"
                                                name={field.name}
                                                checked={field.value === 'true' || field.value === true}
                                                value="true"
                                                onChange={() => field.onChange('true')}
                                            />
                                            <label className="custom-control-label" htmlFor={'standardIsponsorshipYes'}>Yes</label>
                                        </div>
                                        <div className="custom-control custom-radio">
                                            <input
                                                type="radio"
                                                id={'standardIsponsorshipNo'}
                                                className="custom-control-input"
                                                name={field.name}
                                                checked={field.value === 'false' || field.value === false}
                                                value="false"
                                                onChange={() => field.onChange('false')}
                                            />
                                            <label className="custom-control-label" htmlFor={'standardIsponsorshipNo'}>No</label>
                                        </div>
                                    </div>
                                )}
                            />
                            {errors.standardIsponsorship?.message && (
                                <Label className={'text-danger'}>
                                    {errors.standardIsponsorship?.message as string}
                                </Label>
                            )}
                        </CardFormGroup>
                        <CardFormGroup>
                            <h5>Standard II</h5>
                            <div className="program-goals">
                                <Label>Program Goals</Label>
                                <Controller
                                    name="standardIiprogramGoals"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="radio-group">
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'standardIiprogramGoalsYes'} className="custom-control-input" name={field.name} checked={field.value === 'true' || field.value === true} value="true" onChange={() => field.onChange('true')} />
                                                <label className="custom-control-label" htmlFor={'standardIiprogramGoalsYes'}>Yes</label>
                                            </div>
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'standardIiprogramGoalsNo'} className="custom-control-input" name={field.name} checked={field.value === 'false' || field.value === false} value="false" onChange={() => field.onChange('false')} />
                                                <label className="custom-control-label" htmlFor={'standardIiprogramGoalsNo'}>No</label>
                                            </div>
                                        </div>
                                    )}
                                />
                                <div>
                                    {errors.standardIiprogramGoals?.message && (
                                        <Label className={'text-danger'}>
                                            {errors.standardIiprogramGoals?.message as string}
                                        </Label>
                                    )}
                                </div>
                                <Label>{`Does the Advisory Committee have required representation?`}</Label>
                                <Controller
                                    name="advisoryCommitteeRepresentation"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="radio-group">
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'advisoryCommitteeRepresentationYes'} className="custom-control-input" name={field.name} checked={field.value === 'true' || field.value === true} value="true" onChange={() => field.onChange('true')} />
                                                <label className="custom-control-label" htmlFor={'advisoryCommitteeRepresentationYes'}>Yes</label>
                                            </div>
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'advisoryCommitteeRepresentationNo'} className="custom-control-input" name={field.name} checked={field.value === 'false' || field.value === false} value="false" onChange={() => field.onChange('false')} />
                                                <label className="custom-control-label" htmlFor={'advisoryCommitteeRepresentationNo'}>No</label>
                                            </div>
                                        </div>
                                    )}
                                />
                                <div>
                                    {errors.advisoryCommitteeRepresentation?.message && (
                                        <Label className={'text-danger'}>
                                            {errors.advisoryCommitteeRepresentation?.message as string}
                                        </Label>
                                    )}
                                </div>
                                <Label>{`Has the Advisory Committee met at least annually?`}</Label>
                                <Controller
                                    name="standardIIAcHasMetAnnually"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="radio-group">
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'standardIIAcHasMetAnnuallyYes'} className="custom-control-input" name={field.name} checked={field.value === 'true' || field.value === true} value="true" onChange={() => field.onChange('true')} />
                                                <label className="custom-control-label" htmlFor={'standardIIAcHasMetAnnuallyYes'}>Yes</label>
                                            </div>
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'standardIIAcHasMetAnnuallyNo'} className="custom-control-input" name={field.name} checked={field.value === 'false' || field.value === false} value="false" onChange={() => field.onChange('false')} />
                                                <label className="custom-control-label" htmlFor={'standardIIAcHasMetAnnuallyNo'}>No</label>
                                            </div>
                                        </div>
                                    )}
                                />
                                <div>
                                    {errors.standardIIAcHasMetAnnually?.message && (
                                        <Label className={'text-danger'}>
                                            {errors.standardIIAcHasMetAnnually?.message as string}
                                        </Label>
                                    )}
                                </div>
                                <Label>{`Does the Advisory Committee fulfill its required role?`}</Label>
                                <Controller
                                    name="advisoryCommitteeFulfilsRole"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="radio-group">
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'advisoryCommitteeFulfilsRoleYes'} className="custom-control-input" name={field.name} checked={field.value === 'true' || field.value === true} value="true" onChange={() => field.onChange('true')} />
                                                <label className="custom-control-label" htmlFor={'advisoryCommitteeFulfilsRoleYes'}>Yes</label>
                                            </div>
                                            <div className="custom-control custom-radio">
                                                <input type="radio" id={'advisoryCommitteeFulfilsRoleNo'} className="custom-control-input" name={field.name} checked={field.value === 'false' || field.value === false} value="false" onChange={() => field.onChange('false')} />
                                                <label className="custom-control-label" htmlFor={'advisoryCommitteeFulfilsRoleNo'}>No</label>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                            <div>
                                {errors.advisoryCommitteeFulfilsRole?.message && (
                                    <Label className={'text-danger'}>
                                        {errors.advisoryCommitteeFulfilsRole?.message as string}
                                    </Label>
                                )}
                            </div>
                        </CardFormGroup>
                        <CardFormGroup>
                            <h5>Standard III</h5>
                            <Label className="standard-label">Resources</Label>
                            <Controller
                                name="standardIiiresources"
                                control={control}
                                render={({ field }) => (
                                    <div className="radio-group">
                                        <div className="custom-control custom-radio">
                                            <input type="radio" id={'standardIiiresourcesYes'} className="custom-control-input" name={field.name} checked={field.value === 'true' || field.value === true} value="true" onChange={() => field.onChange('true')} />
                                            <label className="custom-control-label" htmlFor={'standardIiiresourcesYes'}>Yes</label>
                                        </div>
                                        <div className="custom-control custom-radio">
                                            <input type="radio" id={'standardIiiresourcesNo'} className="custom-control-input" name={field.name} checked={field.value === 'false' || field.value === false} value="false" onChange={() => field.onChange('false')} />
                                            <label className="custom-control-label" htmlFor={'standardIiiresourcesNo'}>No</label>
                                        </div>
                                    </div>
                                )}
                            />
                            <div>
                                {errors.standardIiiresources?.message && (
                                    <Label className={'text-danger'}>
                                        {errors.standardIiiresources?.message as string}
                                    </Label>
                                )}
                            </div>
                        </CardFormGroup>
                        <CardFormGroup>
                            <h5>Standard IV</h5>
                            <Label className="standard-label">Student and Graduate Evaluation/Assessment</Label>
                            <Controller
                                name="standardIvstudentAndGraduate"
                                control={control}
                                render={({ field }) => (
                                    <div className="radio-group">
                                        <div className="custom-control custom-radio">
                                            <input type="radio" id={'standardIvstudentAndGraduateYes'} className="custom-control-input" name={field.name} checked={field.value === 'true' || field.value === true} value="true" onChange={() => field.onChange('true')} />
                                            <label className="custom-control-label" htmlFor={'standardIvstudentAndGraduateYes'}>Yes</label>
                                        </div>
                                        <div className="custom-control custom-radio">
                                            <input type="radio" id={'standardIvstudentAndGraduateNo'} className="custom-control-input" name={field.name} checked={field.value === 'false' || field.value === false} value="false" onChange={() => field.onChange('false')} />
                                            <label className="custom-control-label" htmlFor={'standardIvstudentAndGraduateNo'}>No</label>
                                        </div>
                                    </div>
                                )}
                            />
                            <div>
                                {errors.standardIvstudentAndGraduate?.message && (
                                    <Label className={'text-danger'}>
                                        {errors.standardIvstudentAndGraduate?.message as string}
                                    </Label>
                                )}
                            </div>
                        </CardFormGroup>
                        <CardFormGroup>
                            <h5>Standard V</h5>
                            <Label className="standard-label">Fair Practice</Label>
                            <Controller
                                name="standardVfairPractices"
                                control={control}
                                render={({ field }) => (
                                    <div className="radio-group">
                                        <div className="custom-control custom-radio">
                                            <input type="radio" id={'standardVfairPracticesYes'} className="custom-control-input" name={field.name} checked={field.value === 'true' || field.value === true} value="true" onChange={() => field.onChange('true')} />
                                            <label className="custom-control-label" htmlFor={'standardVfairPracticesYes'}>Yes</label>
                                        </div>
                                        <div className="custom-control custom-radio">
                                            <input type="radio" id={'standardVfairPracticesNo'} className="custom-control-input" name={field.name} checked={field.value === 'false' || field.value === false} value="false" onChange={() => field.onChange('false')} />
                                            <label className="custom-control-label" htmlFor={'standardVfairPracticesNo'}>No</label>
                                        </div>
                                    </div>
                                )}
                            />
                            <div>
                                {errors.standardVfairPractices?.message && (
                                    <Label className={'text-danger'}>
                                        {errors.standardVfairPractices?.message as string}
                                    </Label>
                                )}
                            </div>
                        </CardFormGroup>
                        {anyNo && (
                            <div className={'d-flex justify-content-between'}>
                                <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                    <Label>Detailed Analysis of Standard(s) Not Met</Label>
                                    <Controller
                                        name="descriptionAnalysis"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="textarea"
                                                id="standardsNotMet"
                                                rows={10}
                                                {...field}
                                                value={field.value ?? standard?.descriptionAnalysis ?? ''}
                                            />
                                        )}
                                    />
                                    <div>
                                        {errors.descriptionAnalysis?.message && (
                                            <Label className={'text-danger'}>
                                                {errors.descriptionAnalysis?.message as string}
                                            </Label>
                                        )}
                                    </div>
                                </FormGroup>
                                <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                    <Label>Action Plan for Standard(s) Not Met</Label>
                                    <Controller
                                        name="actionPlan"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="textarea"
                                                rows={10}
                                                {...field}
                                                value={field.value ?? standard?.actionPlan ?? ''}
                                            />
                                        )}
                                    />
                                    <div>
                                        {errors.actionPlan?.message && (
                                            <Label className={'text-danger'}>
                                                {errors.actionPlan?.message as string}
                                            </Label>
                                        )}
                                    </div>
                                </FormGroup>
                            </div>
                        )}
                    </CardBody>
                    {props.canEdit && (
                        <CardFooter className={'d-flex justify-content-between'}>
                            <Button
                                type={'button'}
                                color={'warning'}
                                onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                            >
                                <i className={'mdi mdi-arrow-left'} />
                                <span className={'mr-1'}>{`Previous`}</span>
                            </Button>

                            <Button
                                disabled={formSubmitting}
                                type={'button'}
                                color={'primary'}
                                onClick={() => submitWithoutValidation()}
                            >
                                <i className={'mdi mdi-content-save'} />
                                <span className={'ml-1'}>{`Save`}</span>
                            </Button>

                            <Button
                                disabled={formSubmitting}
                                type={'submit'}
                                color={'info'}
                                onClick={() => setSubmitDirection(AnnualReportNavigationDirection.Forward)}
                            >
                                <span className={'mr-1'}>{`Save & Continue`}</span>
                                <i className={'mdi mdi-arrow-right'} />
                            </Button>
                        </CardFooter>
                    )}
                </Form>
            )}
        </Card>
    );
};

export default Standards;
